import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  AttendanceCustomTimeRangeDayEnum,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'

import PayrollSetupWarning from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/Modals/PayrollSetupWarning'
import ManageSettingVersionModal from 'modules/attendance/components/SettingItemPage/TimeTracking/ManageSettingVersionModal'
import useFetchData from 'modules/attendance/components/SettingItemPage/TimeTracking/TimeTrackingPolicy/useFetchData'
import { useLazyFetchCustomTimeRangeCategories } from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'
import {
  CustomTimeRange,
  useLazyFetchCustomTimeRanges,
} from 'modules/attendance/hooks/useFetchCustomTimeRanges'
import CreateCustomTimeRangesForm, {
  isPayrolCompensation,
} from 'modules/attendance/lib/forms/CreateCustomTimeRangeFormTimeSettings'
import EditCustomTimeRangesForm from 'modules/attendance/lib/forms/EditCustomTimeRangeFormTimeSettings'

import CompensationTimeBlockModal from './CompensationTimeBlockModal'
import NewTimeBlockModal from './NewTimeBlockModal'
import PayrollSupplementModal from './PayrollSupplementModal'
import { Step } from './utils/stepTypes'
import { validateForm } from './validationUtils'

interface Props {
  onClose: () => void
  settingId: string
  range?: CustomTimeRange
  dayType?: AttendanceCustomTimeRangeDayEnum
}

const AddNewBlockWizard = ({ onClose, settingId, dayType, range }: Props) => {
  const isException = !!range?.isCustom
  const [openCustomOptions, setopenCustomOptions] = React.useState(isException)
  const [step, setStep] = React.useState<Step>(1)
  const [openEffectiveAtFormModal, setOpenEffectiveAtFormModal] = React.useState(false)

  const { fetch: fetchCustomTimeRanges } = useLazyFetchCustomTimeRanges()
  const { fetch: fetchCustomTimeRangeCategories } = useLazyFetchCustomTimeRangeCategories()
  const { currentSetting } = useFetchData(settingId)

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const isEdit = Boolean(range)

  const form = range
    ? EditCustomTimeRangesForm({ settingId, customTimeRange: range })
    : CreateCustomTimeRangesForm({ settingId, dayType })

  const totalSteps = isPayrolCompensation(form) ? 3 : 2

  const onSubmitNextButton = () => {
    const errors = validateForm(form.values, openCustomOptions)
    const hasErrors = Object.values(errors).some(Boolean)

    !hasErrors ? setStep(2) : form.setErrors(errors)
  }

  const submitFormAndCloseModal = () => {
    if (form.dirty) {
      form.handleSubmit()
      onClose()
    } else {
      onClose()
    }
  }

  const onSubmitWizard = () => {
    if (step === 2 && totalSteps === 3) return setStep(3)
    if (isEdit && form.dirty) return setOpenEffectiveAtFormModal(true)

    return submitFormAndCloseModal()
  }

  const goBack = () => {
    if (step === 3) return setStep(2)
    setStep(1)
  }

  const formId = form.values.id
  const formReadyToSubmit = range && formId && String(formId) !== range.id

  // In this method, we're passing the newly created policy version.
  // In the time blocks of the newly created policy version we look for the copy of the time block we modified in the current policy version.
  // We update the time block in the new version with the changes the user submitted in the form.
  const onCreateSetting = async (newSetting: SettingPageEmployeesSettingFragment) => {
    if (!range) return null

    const newSettingId = newSetting.id
    // Fetch the custom time ranges of the newly created setting
    const newCustomTimeRanges = await fetchCustomTimeRanges(newSettingId)

    // Fetch the attendance categories of the current setting
    const currentSettingAttendanceCategories = await fetchCustomTimeRangeCategories(settingId)
    // Fetch the attendance categories of the newly created setting
    const newSettingAttendanceCategories = await fetchCustomTimeRangeCategories(newSettingId)

    // Find the copy of the time block edited in the newly created setting
    const matchingRange = range.findMatchingRange(newCustomTimeRanges.customTimeRanges)

    // Find the attendance category of the time block edited in the current setting
    // attendance_custom_time_range_category_id is now nullable as we're migrating to dev_time_tracking_majoration where it's not required
    const currentSettingAttendanceCategory = currentSettingAttendanceCategories.findCategoryById(
      form.values.attendance_custom_time_range_category_id ?? '0'
    )

    const currentSettingAttendanceCategoryTimeSettingsId =
      currentSettingAttendanceCategory?.timeSettingsCustomTimeRangeCategoryId

    // Find the copy of the current attendance category in the new setting based on its time settings ID
    const newSettingAttendanceCategory =
      newSettingAttendanceCategories.findCategoryByTimeSettingsId(
        currentSettingAttendanceCategoryTimeSettingsId!
      )

    const attrs = {
      id: matchingRange!.id.toString(),
      attendance_custom_time_range_category_id: newSettingAttendanceCategory!.id.toString(),
      day: form.values.day,
      start_time: form.values.start_time,
      end_time: form.values.end_time,
      pool_type: form.values.pool_type,
      attendance_employees_setting_id: newSettingId.toString(),
      reimbursable_taxonomies_mappings: form.values.reimbursable_taxonomies_mappings,
      time_settings_custom_time_range_category_id:
        form.values.time_settings_custom_time_range_category_id,
    }
    form.setValues(attrs)
  }

  useEffect(() => {
    if (formReadyToSubmit) {
      form.submitForm()

      const routeParams = {
        id: 'time_tracking',
        settingId: form.values.attendance_employees_setting_id!.toString(),
        tabId: 'time_ranges',
      }

      navigate(navigation.settings.time_tracking.settingId.tabId.fullPath(routeParams))
    }
  }, [formReadyToSubmit])

  const isPayrollCompensation = isPayrolCompensation(form)
  const hasPayroll = hasFeature('payroll_bundle')

  return (
    <>
      {!openEffectiveAtFormModal && step === 1 && (
        <NewTimeBlockModal
          onClose={onClose}
          form={form}
          onSubmitNextButton={onSubmitNextButton}
          onSubmitWizard={onSubmitWizard}
          openCustomOptions={openCustomOptions}
          setOpenCustomOptions={setopenCustomOptions}
          isEdit={isEdit}
          settingId={settingId}
          step={step}
          totalSteps={totalSteps}
        />
      )}
      {!openEffectiveAtFormModal && step === 2 && (
        <CompensationTimeBlockModal
          form={form}
          onClose={onClose}
          onSubmit={onSubmitWizard}
          goBack={goBack}
          step={step}
          totalSteps={totalSteps}
        />
      )}
      {!openEffectiveAtFormModal && isPayrollCompensation && hasPayroll && step === 3 && (
        <PayrollSupplementModal
          form={form}
          onClose={onClose}
          onSubmit={onSubmitWizard}
          goBack={goBack}
          settingId={settingId}
          step={step}
          totalSteps={totalSteps}
        />
      )}
      {!openEffectiveAtFormModal && isPayrollCompensation && !hasPayroll && step === 3 && (
        <PayrollSetupWarning onCloseModal={onClose} />
      )}
      {openEffectiveAtFormModal && (
        <ManageSettingVersionModal
          onCreate={onCreateSetting}
          onUpdate={submitFormAndCloseModal}
          onClose={onClose}
          running={false}
          attendanceSettingId={currentSetting?.attributes.id}
          attendanceSettingEffectiveAt={currentSetting?.attributes.effective_at}
        />
      )}
    </>
  )
}

export default AddNewBlockWizard
